// src/api/authorizations.ts
import { apiFetch, apiJson } from "@/api/client";
import type {
  AuthorizationDTO,
  AuthorizationTestConnectionResponse,
  TestConnectionResult,
} from "@/types/authorizations";

export async function listAuthorizations(params?: {
  type?: string; // "SQL" | "BIGQUERY"
  limit?: number;
  offset?: number;
}): Promise<AuthorizationDTO[]> {
  const q = new URLSearchParams();
  q.set("limit", String(params?.limit ?? 100));
  q.set("offset", String(params?.offset ?? 0));
  if (params?.type) q.set("type", params.type);

  // apiJson auto-unwraps { data: ... }, returns AuthorizationDTO[] directly
  return apiJson<AuthorizationDTO[]>(`/authorizations?${q.toString()}`);
}

/**
 * Start the Shopify OAuth flow — returns the consent URL to open in a popup and
 * the `state` token to match against the echoed state in the popup message.
 * POST /authorizations/shopify/oauth/start
 */
export async function startShopifyOAuth(
  name: string,
  shopDomain: string,
): Promise<{ authorize_url: string; state: string }> {
  return apiJson<{ authorize_url: string; state: string }>(
    "/authorizations/shopify/oauth/start",
    {
      method: "POST",
      body: JSON.stringify({ name, shop_domain: shopDomain }),
    },
  );
}

/**
 * Start a RECONNECT for an existing Shopify authorization — refresh the token /
 * granted scopes in place (no delete + recreate). Returns the consent URL to
 * open in a popup and the `state` to match. POST /authorizations/shopify/oauth/reconnect
 */
export async function reconnectShopifyOAuth(
  authorizationId: string,
  opts?: { withPersonalization?: boolean; withContent?: boolean },
): Promise<{ authorize_url: string; state: string }> {
  return apiJson<{ authorize_url: string; state: string }>(
    "/authorizations/shopify/oauth/reconnect",
    {
      method: "POST",
      body: JSON.stringify({
        authorization_id: authorizationId,
        // Widen the consent ask to include read_themes (storefront
        // personalization). Omitted for a plain token refresh.
        ...(opts?.withPersonalization ? { with_personalization: true } : {}),
        // Widen it to read_content (site search suggests pages and articles).
        ...(opts?.withContent ? { with_content: true } : {}),
      }),
    },
  );
}

/**
 * Enable Verity-managed storefront personalization for the account — provisions
 * the account's managed KV namespace (idempotent) and reports whether the Shopify
 * connection still needs a read_themes reconnect. `authorizationId` scopes the
 * status to one connection; omit to report across all Shopify connections.
 * POST /authorizations/shopify/personalization/enable
 */
export type ShopifyPersonalizationStatus = {
  enabled: boolean;
  read_themes_granted: boolean;
  reconnect_required: boolean;
  /** The web pixel scopes (write_pixels + read_customer_events) that link
   * express checkouts to campaigns. Missing on stores that granted theme
   * access before they existed; the same grant adds them. Absent on an older
   * backend. */
  checkout_linking_granted?: boolean;
};

/**
 * Which grant the Shopify connection drawer offers: theme access first (the
 * personalization itself needs it), then checkout access (campaign checkout
 * linking). Both start the same personalization reconnect, which asks for
 * every optional scope.
 */
export function personalizationGrantNeeded(
  status: ShopifyPersonalizationStatus | null | undefined,
): "theme" | "checkout" | null {
  if (!status?.enabled) return null;
  if (status.reconnect_required) return "theme";
  if (status.checkout_linking_granted === false) return "checkout";
  return null;
}

/**
 * Set or clear the storefront password on one Shopify connection (needed while
 * a dev/pre-launch store hides its whole storefront behind /password — the
 * section list and content warm can't see anything without it). Write-only:
 * list responses never echo the value, only `config.storefront_password_set`.
 * `null`/empty clears it.
 */
export async function setStorefrontPassword(
  authorizationId: string,
  password: string | null,
): Promise<{ storefront_password_set: boolean }> {
  return apiJson<{ storefront_password_set: boolean }>(
    "/authorizations/shopify/personalization/storefront-password",
    {
      method: "POST",
      body: JSON.stringify({
        authorization_id: authorizationId,
        password: password ?? "",
      }),
    },
  );
}

export async function enableShopifyPersonalization(
  authorizationId?: string,
): Promise<ShopifyPersonalizationStatus> {
  return apiJson<ShopifyPersonalizationStatus>(
    "/authorizations/shopify/personalization/enable",
    {
      method: "POST",
      body: JSON.stringify(
        authorizationId ? { authorization_id: authorizationId } : {},
      ),
    },
  );
}

/**
 * Whether Verity can read the product cost Shopify stores per variant (the
 * `read_inventory` scope). `missing` on connections made before Verity asked
 * for it; a reconnect adds it. `not_applicable_products_off` when no source on
 * the connection syncs products; `unknown` when neither Shopify nor the stored
 * grant can answer. Account admins only (the backend 403s anyone else).
 */
export type ShopifyCostAccess =
  | "granted"
  | "missing"
  | "not_applicable_products_off"
  | "unknown";

export async function getShopifyCostAccess(
  authorizationId: string,
): Promise<ShopifyCostAccess> {
  const res = await apiJson<{ cost_access: ShopifyCostAccess }>(
    `/authorizations/shopify/connections/${encodeURIComponent(authorizationId)}/cost-access`,
  );
  return res.cost_access;
}

/**
 * Whether site search can read this store's pages and blog articles (the
 * optional `read_content` grant). `missing` until the merchant grants it;
 * `not_applicable` when the account has no site search, so the drawer asks
 * for nothing; `unknown` when neither Shopify nor the stored grant can
 * answer. Account admins only (the backend 403s anyone else).
 */
export type ShopifyContentAccess = "granted" | "missing" | "not_applicable" | "unknown";

export async function getShopifyContentAccess(
  authorizationId: string,
): Promise<ShopifyContentAccess> {
  const res = await apiJson<{ content_access: ShopifyContentAccess }>(
    `/authorizations/shopify/connections/${encodeURIComponent(authorizationId)}/content-access`,
  );
  return res.content_access;
}

// --- Storefront personalization: slot → segment → variant mapping (Stap 11) ---

/** One segment→section binding within a slot. Higher `priority` wins on a match. */
export type PersonalizationSlotMapping = {
  segment_id: string; // seg_…
  section_id: string; // Shopify section instance-id
  priority: number;
};

/** A slot's targeting: ordered mappings + an optional default (no-match) section. */
export type PersonalizationSlot = {
  default_section?: string;
  mappings: PersonalizationSlotMapping[];
};

/** The published config document (slots keyed by merchant-named slot handle). */
export type PersonalizationConfig = {
  slots: Record<string, PersonalizationSlot>;
  schema_version: number | null;
  updated_at: string | null;
  /**
   * Server-validated list of profile column names an admin opted into public
   * copy — the "merge tags" a merchant may reference as `[[tag|fallback]]` in
   * theme copy. Defaults to `[]` when the server omits it.
   */
  merge_tags: string[];
  /**
   * Columns an admin MAY enable for public copy — already guardrail-filtered
   * server-side (safe to offer all of them in the opt-in editor). This is the
   * superset the `merge_tags` opt-in is chosen from. Defaults to `[]` when the
   * server omits it.
   */
  exposable_columns: string[];
};

/** A reusable "test persona": a real profile designated for rendered previews. */
export type TestProfile = { visitor_key: string; label: string };

/** A candidate variant section scraped from the merchant's studio page. */
export type StudioSection = { section_id: string; label: string };

/** A KV-enabled segment the merchant can map (public_id + display name). */
export type PersonalizationSegment = { public_id: string; name: string };

/** Result of warming the fragment cache. */
export type PersonalizationWarmSummary = {
  theme_id: string | null;
  requested: number;
  warmed: number;
  truncated?: number;
  sections: Record<string, boolean>;
  /** Per-section placeholder lint of the pushed content. A section whose
   * `refused` list is non-empty was NOT pushed: its content holds an unsafe
   * placeholder. */
  merge_tag_lint?: Record<string, PersonalizationMergeTagLint>;
};

/** Placeholder lint for one warmed section (see `merge_tag_lint`). */
export type PersonalizationMergeTagLint = {
  /** Counts of placeholders found / rewritten for text / URL context. */
  placeholders: number;
  text: number;
  url: number;
  /** Refusal reasons — non-empty means this section was not pushed. */
  refused: string[];
  /** Count of malformed placeholders (left as-is). */
  malformed: number;
};

/** Preview result: the section a segment would get + its warmed HTML (null = not warmed).
 * `css_urls` are the theme's stylesheet URLs captured at warm — the preview
 * iframe loads them so the fragment gets the theme's layout instead of bare
 * markup. */
export type PersonalizationPreview = {
  section_id: string | null;
  html: string | null;
  /** The storefront page's raw head (captured at warm): fonts, stylesheets,
   * scheme variables and theme scripts — the page context a bare fragment
   * lacks. Rendered only inside the preview's opaque-origin sandbox, with the
   * scripts stripped. */
  preview_head?: string | null;
  /** The captured page's `<html>`/`<body>` open-tag attributes (sanitized at
   * warm: no `on*`, capped). Theme CSS keys off body classes — Horizon's
   * `page-width-narrow` drives the section-grid variables — so the srcdoc
   * fallback applies them to its own open tags for layout parity with the
   * isolated-origin render. */
  preview_html_attrs?: Record<string, string>;
  preview_body_attrs?: Record<string, string>;
  /** Short-lived signed URL that serves the SAME composition (head + fragment,
   * scripts intact) from the tenant Worker's own origin — the pixel-perfect
   * preview path. Null when that path isn't available; the UI then renders the
   * stripped srcdoc fallback. Only ever handed to an iframe `src`. */
  preview_url?: string | null;
  /** Which experiment arm this preview actually rendered. Null whenever no arm
   * applied — the campaign runs no experiment, the request named no arm, or the
   * experiment document could not be read and arm 0 was served. Same
   * null-means-"no arm" contract as the edge's meta envelope, so a UI must never
   * present a null arm as a chosen variant. */
  arm?: string | null;
  /** True only for a holdout's `default` arm: the campaign DECIDED to show the
   * storefront's own content, so `section_id` is null on purpose. Distinguishes
   * the control group from "nothing warmed yet", which looks identical. */
  suppressed?: boolean;
};

export interface PreviewUrls {
  /** Section id → signed preview URL (null: Worker unavailable). */
  urls: Record<string, string | null>;
  /** Slot handle → preview URL of the slot's "Storefront default" — the
   * homepage section hosting its app block (null: no warmed mapping). */
  default_urls: Record<string, string | null>;
}

/** Batch-mint isolated-origin preview URLs for the content-picker hover cards.
 * POST /preview-urls — near-pure mint (one KV read only for `slots`), cheap
 * enough to call on every dropdown-open; signatures live ~90s, so refresh by
 * calling again. */
export async function previewUrlsForSections(
  sectionIds: string[],
  slots: string[] = [],
): Promise<PreviewUrls> {
  return apiJson<PreviewUrls>(
    "/authorizations/shopify/personalization/preview-urls",
    {
      method: "POST",
      body: JSON.stringify({
        section_ids: sectionIds,
        ...(slots.length > 0 ? { slots } : {}),
      }),
    },
  );
}

/** Read the currently-published mapping so the UI can hydrate. GET /config */
export async function getPersonalizationConfig(): Promise<PersonalizationConfig> {
  const cfg = await apiJson<PersonalizationConfig>(
    "/authorizations/shopify/personalization/config",
  );
  // Normalize the string-array fields to always be arrays so callers can
  // map/read them without a null-guard (the server sends them, but be defensive).
  return {
    ...cfg,
    merge_tags: Array.isArray(cfg.merge_tags) ? cfg.merge_tags : [],
    exposable_columns: Array.isArray(cfg.exposable_columns)
      ? cfg.exposable_columns
      : [],
  };
}

/**
 * Publish the mapping. POST /config
 *
 * `slots` semantics (2026-08-05 plan, R4-M1 — the server owns the mappings):
 *   - `undefined` OMITS the field: the server carries the currently published
 *     mappings forward unchanged. This is how the merge-tags save works — it
 *     no longer holds (or risks wiping) the slots half of the document.
 *   - an EXPLICIT `{}` clears every mapping. Only the "Clear legacy mappings"
 *     action sends this. Do not confuse the two: omitting where you meant to
 *     clear turns the clear action into a silent no-op (pinned server-side).
 *
 * `mergeTags` is the admin's opt-in of profile columns usable in public copy.
 * The server NARROWS it to the exposable + guardrail-safe subset: the returned
 * `merge_tags` is the source of truth and anything dropped comes back in
 * `rejected_merge_tags` (e.g. reason `"not_exposed"` or `"sensitive:monetary"`).
 * Omitting `mergeTags` sends `[]`, which clears the opt-in.
 */
export async function setPersonalizationConfig(
  slots: Record<string, PersonalizationSlot> | undefined,
  mergeTags?: string[],
  frontendId?: string,
): Promise<{
  schema_version: number;
  updated_at: string;
  slot_count: number;
  merge_tags: string[];
  rejected_merge_tags: Array<{ column: string; reason: string }>;
}> {
  const body: Record<string, unknown> = { merge_tags: mergeTags ?? [] };
  if (slots !== undefined) body.slots = slots;
  // ADR-0182: WHICH storefront's config. Omitted = the account's sole one
  // (transitional; the backend refuses loudly at >=2 without a choice).
  if (frontendId) body.frontend_id = frontendId;
  return apiJson("/authorizations/shopify/personalization/config", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Why the section list is what it is. `page_not_found` = the studio page
 * 404s (deleted/unpublished — retrying won't help); `unreachable` = a redirect,
 * 5xx or transport error (temporary — retrying helps); `page_empty` = the page
 * was reached but holds no content sections. Only `page_empty` and `ok` make
 * an empty list mean "there are none". */
export type StudioSectionsReason =
  | "ok"
  | "page_empty"
  | "page_not_found"
  | "unreachable";

export type StudioSectionList = {
  sections: StudioSection[];
  reason: StudioSectionsReason;
};

/**
 * List candidate variant sections from a studio page. GET /sections
 *
 * `campaignId` selects WHICH page: a campaign with its own studio page must show
 * that page's sections, not the shared default's. Omit it for the default page.
 * The path itself is never sent — the backend resolves the campaign against the
 * session's account and uses its stored path.
 */
export async function listPersonalizationSections(
  authorizationId?: string,
  campaignId?: string,
): Promise<StudioSectionList> {
  const params = new URLSearchParams();
  if (authorizationId) params.set("authorization_id", authorizationId);
  if (campaignId) params.set("campaign_id", campaignId);
  const q = params.toString() ? `?${params.toString()}` : "";
  const res = await apiJson<StudioSectionList>(
    `/authorizations/shopify/personalization/sections${q}`,
  );
  return { sections: res.sections, reason: res.reason };
}

/** List the account's KV-enabled segments for the dropdown. GET /segments */
export async function listPersonalizationSegments(): Promise<
  PersonalizationSegment[]
> {
  const res = await apiJson<{ segments: PersonalizationSegment[] }>(
    "/authorizations/shopify/personalization/segments",
  );
  return res.segments;
}

export type PersonalizationDestination = {
  public_id: string;
  name: string;
  data_source_id: string;
  data_source_name: string;
  schedule_type: string;
  /** Which storefront this destination delivers to (null for a pre-backfill row). */
  frontend_id?: string | null;
  frontend_name?: string | null;
  /** False = this source writes no visitor key the storefront reads, so every
   * visitor gets the default content. Null when the storefront is unknown. */
  reaches_storefront?: boolean | null;
};

/** Per Shopify storefront, one profile source of the account and whether it can
 * personalize that storefront. The UI offers only the `reaches_storefront`
 * ones, so it never restates the backend's rule. */
export type PersonalizationSourceOption = {
  frontend_id: string;
  data_source_id: string;
  data_source_name: string;
  reaches_storefront: boolean;
};

export type PersonalizationRouting = {
  destinations: PersonalizationDestination[];
  /** Absent on an older backend; callers then fall back to their own filter. */
  source_options?: PersonalizationSourceOption[];
};

/** Segment-routing state: the managed KV destinations plus, per storefront,
 * which sources can personalize it. GET /destination */
export async function listPersonalizationDestinations(): Promise<PersonalizationRouting> {
  const res = await apiJson<PersonalizationRouting>(
    "/authorizations/shopify/personalization/destination",
  );
  return {
    destinations: res.destinations ?? [],
    ...(Array.isArray(res.source_options) ? { source_options: res.source_options } : {}),
  };
}

/** Route segments to personalization: create the managed KV destination. POST /destination */
export async function createPersonalizationDestination(
  dataSourceId: string,
  frontendId?: string,
): Promise<{ public_id: string; name: string; created: boolean }> {
  return apiJson("/authorizations/shopify/personalization/destination", {
    method: "POST",
    body: JSON.stringify({
      data_source_id: dataSourceId,
      ...(frontendId ? { frontend_id: frontendId } : {}),
    }),
  });
}

/**
 * Change which profile source routes segments to a storefront. PATCH /destination
 *
 * Refusals carry a customer-facing `detail` meant to be shown verbatim: 422
 * (the source can't recognise this storefront's visitors), 409 (segments are
 * still routed, or live campaigns use segments of the current source; the
 * detail names them), 404 (no routing yet), 403 (account admin required).
 */
export async function changePersonalizationDestinationSource(
  dataSourceId: string,
  frontendId?: string,
): Promise<{
  public_id: string;
  name: string;
  data_source_id: string | null;
  data_source_name: string | null;
  changed: boolean;
}> {
  return apiJson("/authorizations/shopify/personalization/destination", {
    method: "PATCH",
    body: JSON.stringify({
      data_source_id: dataSourceId,
      ...(frontendId ? { frontend_id: frontendId } : {}),
    }),
  });
}

/** Warm the fragment cache (render every configured variant). POST /warm */
/**
 * Warm the fragment cache. `sectionIds` NARROWS the warm to those sections
 * (server-side intersection with the configured set — an unknown id is ignored,
 * never rendered); omit it to warm everything configured. The campaign detail
 * page warms only its own campaign's sections through this, because a full warm
 * renders the whole account against Shopify inside a 12/hour budget.
 */
export async function warmPersonalization(
  authorizationId?: string,
  sectionIds?: string[],
  frontendId?: string,
): Promise<PersonalizationWarmSummary> {
  const body: Record<string, unknown> = {};
  if (authorizationId) body.authorization_id = authorizationId;
  if (sectionIds !== undefined) body.section_ids = sectionIds;
  if (frontendId) body.frontend_id = frontendId;
  return apiJson<PersonalizationWarmSummary>(
    "/authorizations/shopify/personalization/warm",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** Preview how a segment sees a slot (11b). POST /preview */
/**
 * Preview a slot as a simulated visitor.
 *
 * `segments` is the visitor's WHOLE segment set — a campaign variation's rule
 * can require several memberships (`seg_a AND seg_b`), which the old singular
 * form was structurally unable to satisfy (it always previewed the fallback for
 * such rules). `[]` previews the anonymous visitor. The legacy single-segment
 * signature remains available via `previewPersonalizationLegacy` until the last
 * mapping-era caller is gone.
 */
export async function previewPersonalization(
  slot: string,
  segments: string[],
  /** Simulate a visitor in this experiment arm. Omitted = whatever arm 0 is —
   * the preview never rolls a real assignment, because the salt is server-side
   * and a preview must be reproducible. */
  arm?: string | null,
): Promise<PersonalizationPreview> {
  return apiJson<PersonalizationPreview>(
    "/authorizations/shopify/personalization/preview",
    {
      method: "POST",
      // `arm` is omitted rather than sent as null when absent: the endpoint
      // predates arms and an older backend rejects unknown keys, so a preview
      // of a campaign without an experiment keeps sending exactly what it sent
      // before.
      body: JSON.stringify(
        arm ? { slot, segment_ids: segments, arm } : { slot, segment_ids: segments },
      ),
    },
  );
}

/** The pre-campaign single-segment form. `undefined`/"default" = anonymous. */
export async function previewPersonalizationLegacy(
  slot: string,
  segmentId?: string,
): Promise<PersonalizationPreview> {
  return apiJson<PersonalizationPreview>(
    "/authorizations/shopify/personalization/preview",
    {
      method: "POST",
      body: JSON.stringify({ slot, segment_id: segmentId ?? null }),
    },
  );
}

// --- Test profiles (reusable "test personas") + rendered preview (D7) ---

/** List the account's designated test profiles (keyed on public visitor_key). */
export async function listTestProfiles(): Promise<TestProfile[]> {
  return apiJson<TestProfile[]>(
    "/authorizations/shopify/personalization/test-profiles",
  );
}

/**
 * Designate a real profile as a reusable test persona. `visitor_key` is the
 * public cookie id (never an internal id). The server validates the key shape
 * (422) and enforces a cap of 25 (409). `created` is false when the key was
 * already designated (idempotent re-add).
 */
export async function createTestProfile(
  visitor_key: string,
  label: string,
): Promise<{ visitor_key: string; label: string; created: boolean }> {
  return apiJson("/authorizations/shopify/personalization/test-profiles", {
    method: "POST",
    body: JSON.stringify({ visitor_key, label }),
  });
}

/** Remove a designated test profile. DELETE ?visitor_key=<key> */
export async function deleteTestProfile(
  visitor_key: string,
): Promise<{ deleted: boolean }> {
  return apiJson(
    `/authorizations/shopify/personalization/test-profiles?visitor_key=${encodeURIComponent(
      visitor_key,
    )}`,
    { method: "DELETE" },
  );
}

/** The mode-3 (test-profile) preview response. */
export type RenderedPreview = {
  html: string | null;
  /** Short-lived signed URL for the isolated-origin preview of this same
   * render — see `PersonalizationPreview.preview_url`. It is minted before the
   * server calls the Worker, so it also comes back when the backend couldn't
   * reach the Worker but the merchant's browser can. */
  preview_url?: string | null;
};

/**
 * Render a slot through the tenant's real Worker for one test profile — returns
 * the FULLY-SUBSTITUTED HTML, or `html: null` when the Worker reports no
 * personalization (204) or the backend couldn't reach it. 404 = not a
 * designated test profile; 409 = personalization isn't fully provisioned yet.
 */
export async function previewRendered(
  slot: string,
  visitor_key: string,
): Promise<RenderedPreview> {
  return apiJson<RenderedPreview>(
    "/authorizations/shopify/personalization/preview/rendered",
    {
      method: "POST",
      body: JSON.stringify({ slot, visitor_key }),
    },
  );
}

/**
 * Provision (create-or-replace) an account's Shopify custom-distribution app
 * credentials. Superadmin-only (the backend enforces `require_superadmin`).
 * The client secret is encrypted server-side with the target account's key and
 * is never echoed back. POST /authorizations/shopify/apps
 */
export async function provisionShopifyApp(input: {
  account_public_id: string;
  client_id: string;
  client_secret: string;
  shop_domains: string[];
}): Promise<{ account_public_id: string; client_id: string; shop_domains: string[] }> {
  return apiJson<{ account_public_id: string; client_id: string; shop_domains: string[] }>(
    "/authorizations/shopify/apps",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

/** Non-secret read-back of an account's provisioned Shopify app, so the superadmin
 * form can hydrate on load. The client secret is NEVER returned (write-only). */
export type ShopifyAppConfig = {
  configured: boolean;
  client_id: string | null;
  shop_domains: string[];
  secret_set: boolean;
};

/** GET /authorizations/shopify/apps?account_public_id=… (superadmin) */
export async function getShopifyApp(
  accountPublicId: string,
): Promise<ShopifyAppConfig> {
  return apiJson<ShopifyAppConfig>(
    `/authorizations/shopify/apps?account_public_id=${encodeURIComponent(accountPublicId)}`,
  );
}

/**
 * Complete the Shopify OAuth flow — `query` is the raw callback querystring the
 * popup relayed. The backend verifies the HMAC, exchanges the code, and creates
 * the Authorization. POST /authorizations/shopify/oauth/exchange
 */
export async function exchangeShopifyOAuth(
  query: string,
): Promise<{ authorization_id: string; shop_name: string; currency_code: string }> {
  return apiJson<{ authorization_id: string; shop_name: string; currency_code: string }>(
    "/authorizations/shopify/oauth/exchange",
    {
      method: "POST",
      body: JSON.stringify({ query }),
    },
  );
}

export async function getAuthorizationSources(authorizationId: string) {
  // apiJson auto-unwraps { data: { authorization_id, sources: [...] } }
  return apiJson<{ authorization_id: string; sources: any[] }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/sources`
  );
}

export async function updateAuthorization(
  authorizationId: string,
  payload: { name?: string }
): Promise<AuthorizationDTO> {
  // apiJson auto-unwraps { data: AuthorizationDTO }
  return apiJson<AuthorizationDTO>(
    `/authorizations/${encodeURIComponent(authorizationId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteAuthorization(authorizationId: string) {
  await apiJson<void>(
    `/authorizations/${encodeURIComponent(authorizationId)}`,
    { method: "DELETE" }
  );
}

// ---- Facebook OAuth re-authentication ----

/**
 * GET /authorizations/{id}/reauth/facebook
 *
 * Starts a re-auth flow for an existing OAuth-backed Facebook
 * authorization. Returns the Facebook consent URL plus a signed state
 * token that must be echoed back to submitFacebookReauth.
 */
export async function getFacebookReauthUrl(
  authorizationId: string,
): Promise<{ url: string; state: string }> {
  return apiJson<{ url: string; state: string }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/reauth/facebook`,
  );
}

/**
 * POST /authorizations/{id}/reauth/facebook
 *
 * Completes the re-auth by exchanging the OAuth code and updating the
 * existing authorization in place. Rejects scope-narrowing: if
 * `missing_scopes` is returned the UI should render it verbatim.
 */
export async function submitFacebookReauth(
  authorizationId: string,
  code: string,
  state: string,
): Promise<{ authorization_id: string; status: string }> {
  return apiJson<{ authorization_id: string; status: string }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/reauth/facebook`,
    {
      method: "POST",
      body: JSON.stringify({ code, state }),
    },
  );
}

/**
 * POST /authorizations/{id}/test-connection
 *
 * Probes a stored authorization against its upstream (Facebook Graph API
 * today) without requiring new credentials. Superadmin UI affordance.
 *
 * Normalizes three response shapes into a single TestConnectionResult:
 *   1. 2xx/4xx structured body (AuthorizationTestConnectionResponse)
 *   2. 500 plain FastAPI `{detail: "..."}`  → synthesized error body
 *   3. non-JSON infra error (502/504 HTML)  → synthesized error body
 *
 * The caller branches on `result.status` / `result.http_status` and never
 * touches the raw Response.
 */
export async function testAuthorizationConnection(
  authorizationId: string,
): Promise<TestConnectionResult> {
  const res = await apiFetch(
    `/authorizations/${encodeURIComponent(authorizationId)}/test-connection`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );

  let raw: unknown = null;
  try {
    raw = await res.json();
  } catch {
    raw = null;
  }

  const isStructured =
    raw !== null &&
    typeof raw === "object" &&
    "status" in (raw as Record<string, unknown>) &&
    ((raw as Record<string, unknown>).status === "ok" ||
      (raw as Record<string, unknown>).status === "error");

  if (isStructured) {
    return {
      ...(raw as AuthorizationTestConnectionResponse),
      http_status: res.status,
    };
  }

  // Fallback: plain {detail: "..."} (500) or non-JSON (502/504)
  const detailMessage =
    raw &&
    typeof raw === "object" &&
    typeof (raw as Record<string, unknown>).detail === "string"
      ? ((raw as Record<string, unknown>).detail as string)
      : `HTTP ${res.status}: unable to parse response body`;

  return {
    authorization_id: authorizationId,
    type: "",
    status: "error",
    detail: null,
    error: {
      stage: "list_accounts",
      code: null,
      message: detailMessage,
      missing_scopes: [],
    },
    http_status: res.status,
  };
}

// Cloudflare KV

export type CloudflareKvNamespace = {
  id: string;
  title: string;
  supports_url_encoding: boolean;
};

/**
 * POST /authorizations/cloudflare-kv/list-namespaces
 *
 * Lists Workers-KV namespaces using credentials supplied in the body —
 * called by the create-flow wizard BEFORE an authorization row exists.
 * Capped at 10 pages (1000 namespaces) by the backend; the `truncated`
 * flag tells the UI whether to render the "showing first N" hint.
 *
 * Pass an `AbortSignal` to cancel an in-flight request when the user
 * edits credentials and re-clicks Load — defends against the
 * slow-first / fast-second race that would populate the Select with
 * stale namespaces.
 */
export async function listCloudflareKvNamespacesPreCreate(
  body: { api_token: string; cf_account_id: string },
  signal?: AbortSignal,
): Promise<{ data: CloudflareKvNamespace[]; truncated: boolean }> {
  return apiJson("/authorizations/cloudflare-kv/list-namespaces", {
    method: "POST",
    body: JSON.stringify(body),
    unwrapData: false,
    signal,
  });
}

/**
 * POST /authorizations/cloudflare-kv
 *
 * Creates a Cloudflare KV authorization. The backend re-runs the
 * connection test server-side as fail-fast, so the wizard skips an
 * intermediate /test round-trip.
 */
export async function createCloudflareKvAuthorization(payload: {
  name: string;
  api_token: string;
  cf_account_id: string;
  namespace_id: string;
}): Promise<AuthorizationDTO> {
  return apiJson<AuthorizationDTO>("/authorizations/cloudflare-kv", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Custom KV (HTTP) endpoint

/**
 * POST /authorizations/http-kv/test
 *
 * Probes a customer-hosted HTTP key-value endpoint with the api_key (the
 * backend runs the SSRF guard + a connectivity check). Returns the
 * canonicalized endpoint_base on success; throws ApiError otherwise.
 */
export async function testHttpKvEndpoint(payload: {
  endpoint_base: string;
  api_key: string;
}): Promise<{ endpoint_base: string }> {
  return apiJson<{ endpoint_base: string }>("/authorizations/http-kv/test", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * POST /authorizations/http-kv
 *
 * Creates a Custom KV endpoint authorization. The backend re-runs the
 * connection probe server-side as fail-fast.
 */
export async function createHttpKvAuthorization(payload: {
  name: string;
  endpoint_base: string;
  api_key: string;
}): Promise<AuthorizationDTO> {
  return apiJson<AuthorizationDTO>("/authorizations/http-kv", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Brevo

export type BrevoList = {
  id: number;
  name: string;
  total_subscribers: number;
};

export type BrevoAttribute = {
  name: string;
  category: string;
  type: string | null;
};

export async function testBrevoCredentials(apiKey: string): Promise<{
  account_email: string;
  company_name: string;
  plan: string;
}> {
  return apiJson("/authorizations/brevo/test", {
    method: "POST",
    body: JSON.stringify({ api_key: apiKey }),
  });
}

export type BrevoCreatePayload = {
  name: string;
  apiKey: string;
};

/** Replace the API key on an existing Brevo authorization. The backend verifies
 *  the new key resolves to the same Brevo account and auto-resumes any sources
 *  that were paused because the old key went invalid. */
export async function rotateBrevoKey(
  authorizationId: string,
  apiKey: string,
): Promise<unknown> {
  return apiJson(
    `/authorizations/brevo/${encodeURIComponent(authorizationId)}/rotate-key`,
    { method: "PUT", body: JSON.stringify({ api_key: apiKey }) },
  );
}

// ActiveCampaign

export type ActiveCampaignCredentials = {
  apiUrl: string;
  apiKey: string;
};

export type ActiveCampaignTestResult = {
  api_url: string;
  account: string;
};

/** Validate an ActiveCampaign API URL + key. A 400 carries a `detail` saying
 *  why the URL or key cannot be used; `apiJson` surfaces it as the error message. */
export async function testActiveCampaignCredentials(
  creds: ActiveCampaignCredentials,
): Promise<ActiveCampaignTestResult> {
  return apiJson("/authorizations/activecampaign/test", {
    method: "POST",
    body: JSON.stringify({ api_url: creds.apiUrl, api_key: creds.apiKey }),
  });
}

export type ActiveCampaignCreatePayload = ActiveCampaignCredentials & {
  name: string;
};

export async function createActiveCampaignAuthorization(
  payload: ActiveCampaignCreatePayload,
): Promise<AuthorizationDTO> {
  return apiJson("/authorizations/activecampaign", {
    method: "POST",
    body: JSON.stringify({
      name: payload.name,
      api_url: payload.apiUrl,
      api_key: payload.apiKey,
    }),
  });
}

/** Replace the API key on an existing ActiveCampaign authorization. The backend
 *  verifies the new key resolves to the same account and auto-resumes any
 *  sources that were paused because the old key went invalid. */
export async function rotateActiveCampaignKey(
  authorizationId: string,
  apiKey: string,
): Promise<unknown> {
  return apiJson(
    `/authorizations/activecampaign/${encodeURIComponent(authorizationId)}/rotate-key`,
    { method: "PUT", body: JSON.stringify({ api_key: apiKey }) },
  );
}

/** Set the event tracking account ID and event key on an ActiveCampaign
 *  authorization (journey event steps send events with them). The backend
 *  checks their shape and that event tracking is turned on in ActiveCampaign;
 *  the event key is never returned, only `config.event_tracking_actid`. */
export async function setActiveCampaignEventTracking(
  authorizationId: string,
  actid: string,
  eventKey: string,
): Promise<unknown> {
  return apiJson(
    `/authorizations/activecampaign/${encodeURIComponent(authorizationId)}/event-tracking`,
    { method: "PUT", body: JSON.stringify({ actid, event_key: eventKey }) },
  );
}

export async function testKlaviyoCredentials(apiKey: string): Promise<{
  klaviyo_account_id: string;
  organization_name: string;
  test_account: boolean;
}> {
  return apiJson("/authorizations/klaviyo/test", {
    method: "POST",
    body: JSON.stringify({ api_key: apiKey }),
  });
}

/** Replace the API key on an existing Klaviyo authorization (same-account
 *  verified + auto-resume, backend-side). */
export async function rotateKlaviyoKey(
  authorizationId: string,
  apiKey: string,
): Promise<unknown> {
  return apiJson(
    `/authorizations/klaviyo/${encodeURIComponent(authorizationId)}/rotate-key`,
    { method: "PUT", body: JSON.stringify({ api_key: apiKey }) },
  );
}

export type KlaviyoCreatePayload = {
  name: string;
  apiKey: string;
};

export async function createKlaviyoAuthorization(
  payload: KlaviyoCreatePayload
): Promise<AuthorizationDTO> {
  return apiJson("/authorizations/klaviyo", {
    method: "POST",
    body: JSON.stringify({
      name: payload.name,
      api_key: payload.apiKey,
    }),
  });
}

// Generic credential form (API-key connectors, catalog plan step 0.5).
// A connector whose catalogue item has `has_credential_form` connects through
// these four calls; there is no per-vendor function.

export type CredentialFieldKind =
  | "secret"
  | "text"
  | "host_label"
  | "customer_origin"
  /** A yes/no choice made at connect; sent as "true" or "false". */
  | "toggle";

export type CredentialField = {
  name: string;
  label: string;
  kind: CredentialFieldKind;
  help: string;
  placeholder: string;
  required: boolean;
  /** Fixed host suffix a `host_label` value is joined to (".myshop.com"). */
  host_suffix?: string | null;
  max_length: number;
  /** A `toggle`'s starting value. */
  default?: boolean | null;
};

export type CredentialForm = {
  connector_type: string;
  label: string;
  instructions: string;
  fields: CredentialField[];
};

export type CredentialTestResult = {
  /** Customer-readable sentence about what the test reached. */
  summary: string;
  /** Suggested connection name (e.g. the store's host). */
  default_name: string;
  /** Set when the connection works but the customer should change something
   *  (an integration holding a permission Verity did not ask for). */
  warning?: string;
};

/** Values keyed by the form's field names. */
export type CredentialValues = Record<string, string>;

function connectorPath(connectorType: string): string {
  return `/authorizations/connectors/${encodeURIComponent(connectorType)}`;
}

/** The fields a connector's connect form renders. Throws an `ApiError`
 *  (status 404) for a connector without a credential form. */
export async function getCredentialForm(
  connectorType: string,
): Promise<CredentialForm> {
  return apiJson(`${connectorPath(connectorType)}/credential-form`);
}

/** Check credentials without saving. A failure throws an `ApiError` whose
 *  message is the backend's customer-readable diagnosis. */
export async function testConnectorCredentials(
  connectorType: string,
  values: CredentialValues,
): Promise<CredentialTestResult> {
  return apiJson(`${connectorPath(connectorType)}/test`, {
    method: "POST",
    body: JSON.stringify({ values }),
  });
}

export async function createConnectorAuthorization(
  connectorType: string,
  payload: { name: string; values: CredentialValues },
): Promise<AuthorizationDTO> {
  return apiJson(connectorPath(connectorType), {
    method: "POST",
    body: JSON.stringify({ name: payload.name, values: payload.values }),
  });
}

/** Replace the secret fields on an existing authorization. The backend tests
 *  them against the stored (non-secret) values and the same vendor account. */
export async function rotateConnectorCredentials(
  authorizationId: string,
  values: CredentialValues,
): Promise<AuthorizationDTO> {
  return apiJson(
    `/authorizations/${encodeURIComponent(authorizationId)}/credentials`,
    { method: "PUT", body: JSON.stringify({ values }) },
  );
}

export async function createBrevoAuthorization(
  payload: BrevoCreatePayload
): Promise<AuthorizationDTO> {
  return apiJson("/authorizations/brevo", {
    method: "POST",
    body: JSON.stringify({
      name: payload.name,
      api_key: payload.apiKey,
    }),
  });
}

export async function listBrevoLists(
  authorizationId: string
): Promise<BrevoList[]> {
  const result = await apiJson<{ lists: BrevoList[] }>(
    `/authorizations/brevo/lists?authorization_id=${encodeURIComponent(authorizationId)}`
  );
  return result.lists;
}

export async function listBrevoAttributes(
  authorizationId: string
): Promise<BrevoAttribute[]> {
  const result = await apiJson<{ attributes: BrevoAttribute[] }>(
    `/authorizations/brevo/attributes?authorization_id=${encodeURIComponent(authorizationId)}`
  );
  return result.attributes;
}

// BigQuery browsing

export type BqProject = {
  project_id: string;
  display_name: string;
  /**
   * Enumeration FAILED and this is a guess: the backend could not list the
   * projects this authorization can reach and returned the service account's
   * own project instead. Never auto-select from a list containing these — it
   * would silently configure the wrong project. Let the user choose.
   */
  is_fallback?: boolean;
  /**
   * This authorization has exactly this project, BY CONFIGURATION (runtime-SA /
   * IAM-grant authorizations store their project in the authorization itself).
   * Authoritative — the opposite of `is_fallback`, never set together with it.
   * Render it as a read-only value rather than a one-option dropdown: a
   * disabled dropdown reads as "nothing was found", which is the failure
   * appearance of `is_fallback`.
   */
  is_fixed?: boolean;
};

export type BqDataset = {
  dataset_id: string;
  project_id: string;
  location: string;
};

export type BqTable = {
  table_id: string;
  dataset_id: string;
  project_id: string;
  table_type: string;
};

export async function listBigQueryProjects(
  authorizationId: string
): Promise<BqProject[]> {
  const result = await apiJson<{ projects: BqProject[] }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/bigquery/projects`
  );
  return result.projects;
}

export async function listBigQueryDatasets(
  authorizationId: string,
  projectId?: string
): Promise<BqDataset[]> {
  const q = new URLSearchParams();
  if (projectId) q.set("project_id", projectId);
  const qs = q.toString();

  const result = await apiJson<{ datasets: BqDataset[] }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/bigquery/datasets${qs ? `?${qs}` : ""}`
  );
  return result.datasets;
}

export async function listBigQueryTables(
  authorizationId: string,
  datasetId: string,
  projectId?: string
): Promise<BqTable[]> {
  const q = new URLSearchParams();
  q.set("dataset_id", datasetId);
  if (projectId) q.set("project_id", projectId);

  const result = await apiJson<{ tables: BqTable[] }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/bigquery/tables?${q.toString()}`
  );
  return result.tables;
}

export type BqColumn = {
  column_name: string;
  data_type: string;
  is_nullable: boolean;
  description: string | null;
};

export async function listBigQueryColumns(
  authorizationId: string,
  datasetId: string,
  tableId: string,
  projectId?: string
): Promise<BqColumn[]> {
  const q = new URLSearchParams();
  q.set("dataset_id", datasetId);
  q.set("table_id", tableId);
  if (projectId) q.set("project_id", projectId);

  const result = await apiJson<{ columns: BqColumn[] }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/bigquery/columns?${q.toString()}`
  );
  return result.columns;
}

export async function listBigQueryAllDatasetsAndTables(
  authorizationId: string,
  projectId?: string,
  refresh?: boolean
): Promise<{ datasets: BqDataset[]; tables: BqTable[]; fetched_at: string }> {
  const q = new URLSearchParams();
  if (projectId) q.set("project_id", projectId);
  if (refresh) q.set("refresh", "true");
  const qs = q.toString();

  return apiJson<{ datasets: BqDataset[]; tables: BqTable[]; fetched_at: string }>(
    `/authorizations/${encodeURIComponent(authorizationId)}/bigquery/all-tables${qs ? `?${qs}` : ""}`
  );
}

/**
 * GET /authorizations/cloudflare-kv/namespaces?authorization_id=auth_xxx
 *
 * Lists the Workers KV namespaces reachable through an authorization that
 * ALREADY EXISTS — the picker uses it once a saved Cloudflare KV authorization
 * has been chosen.
 *
 * Its POST sibling `listCloudflareKvNamespacesPreCreate` answers the same
 * question one step earlier, from raw credentials, while the authorization row
 * has not been written yet. Neither replaces the other: there is no id to send
 * before the row exists, and re-sending the token after it exists would mean
 * asking the user for a secret the account already stores. Both are needed.
 */
export async function listCloudflareKvNamespacesForAuthorization(
  authorizationId: string,
): Promise<CloudflareKvNamespace[]> {
  const res = await apiJson<{ namespaces: CloudflareKvNamespace[] }>(
    `/authorizations/cloudflare-kv/namespaces?authorization_id=${encodeURIComponent(
      authorizationId,
    )}`,
  );
  return res.namespaces ?? [];
}
